import {times} from 'lodash';
import React, {useCallback, useEffect, useRef} from 'react';

type UseDebounceOptions = {
  cancelOnUnmount?: boolean;
};
export function useDebounce<A extends any[]>(
  _callback: (...args: A) => void,
  ms: number,
  options: UseDebounceOptions = {}
): (...args: A) => void {
  const callbackRef = useRef(_callback);
  callbackRef.current = _callback;
  const lastArgsRef = useRef([] as unknown as A);
  const runningTimeout = useRef(null as number | null);
  const callback = useCallback(() => {
    callbackRef.current(...lastArgsRef.current);
    runningTimeout.current = null;
    lastArgsRef.current = [] as unknown as A;
  }, []);
  const debounced = useCallback(
    (...args: A) => {
      if (runningTimeout.current === null) {
        runningTimeout.current = window.setTimeout(callback, ms);
      }
      lastArgsRef.current = args;
    },
    [callback, ms]
  );
  const optionsRef = useRef(options);
  optionsRef.current.cancelOnUnmount = options.cancelOnUnmount ?? true;
  useEffect(() => {
    return () => {
      // eslint-disable-next-line
      if (optionsRef.current.cancelOnUnmount) {
        window.clearTimeout(runningTimeout.current ?? undefined);
        runningTimeout.current = null;
      }
    };
  }, []);
  return debounced;
}

function useRefWithCallback<E extends HTMLElement>(callback: (node: E | null, prevNode: E | null) => void, deps: React.DependencyList) {
  const ref = useRef<E | null>(null);
  const setRef = useCallback((node: E | null) => {
    callback(node, ref.current);
    ref.current = node;
    // eslint-disable-next-line
    }, deps);

  return {setRef, __ref: ref};
}
type OnMountProps<E extends HTMLElement> = {
  onMount?: (node: E) => void;
  onUnMount?: (prevNode: E | null) => void;
};
export function useOnMount<E extends HTMLElement>(props: OnMountProps<E>) {
  const {onMount, onUnMount} = props;
  return useRefWithCallback(
    (node: E | null, prevNode: E | null) => {
      if (node !== null && onMount) onMount(node);
      else if (onUnMount) onUnMount(prevNode);
    },
    [onMount, onUnMount]
  );
}

export type DefaultScroll = {
  to?: number;
  toX?: number;
  dependOn?: any;
  debug?: boolean;
};
let __useDefaultScrollX: DOMRect | undefined;
export function useDefaultScroll(elementRef: React.MutableRefObject<HTMLElement | null>, defaultScroll: DefaultScroll | undefined) {
  useEffect(() => {
    if (!defaultScroll) return () => 0;
    const interval = setInterval(() => {
      if (defaultScroll.debug) console.log('defaultScroll.1', elementRef?.current);
      if (!elementRef?.current) return;
      const isLoadingA =
        elementRef.current.querySelector("div[class*='dlLoadingSpinnerWrapper']:not([data-hidable='true']), .MuiCircularProgress-root") !==
        null;
      // NOTE: material-table has scrollRef deep down, but loading spinner high up
      const someAncestor = elementRef.current.parentElement?.parentElement?.parentElement;
      const isLoadingB = someAncestor && someAncestor.querySelector('.MuiCircularProgress-root') !== null;
      const isLoading = isLoadingA || isLoadingB;
      if (defaultScroll.debug) console.log('defaultScroll.2', isLoading);
      if (!isLoading) {
        clearInterval(interval);
        setTimeout(() => {
          if (!elementRef.current || !(defaultScroll.toX || defaultScroll.to)) return;
          const doTheScroll = () => {
            if (elementRef.current) {
              __useDefaultScrollX = elementRef.current.getBoundingClientRect(); // NOTE: force browser layout
              elementRef.current.scrollTo(defaultScroll.toX ?? 0, defaultScroll.to ?? 0);
            }
          };
          doTheScroll();
          // setTimeout(() => {
          //  doTheScroll(); // NOTE: soft fix for isLoading race conditions
          // }, 100);
        }, 250);
      }
    }, 250);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, [defaultScroll?.dependOn]);
}
export function useDefaultScrollTo(defaultScrollTo: number | undefined) {
  const ref = useRef(null as HTMLDivElement | null);
  useDefaultScroll(ref, {to: defaultScrollTo, dependOn: null});
  return ref;
}
export function useScrollTo(scrollTo: number | undefined) {
  const ref = useRef(null as HTMLDivElement | null);
  useDefaultScroll(ref, {to: scrollTo, dependOn: scrollTo});
  return ref;
}
export function useScrollTos(N: number, scrollTos: number[] | undefined) {
  // NOTE: N must be constant during the hook usage
  return times(N, (i) => useScrollTo(scrollTos?.[i])); // eslint-disable-line react-hooks/rules-of-hooks
}
export function getScrollOffset(scrollBox: HTMLDivElement | undefined | null, element: HTMLElement | undefined | null) {
  if (!scrollBox || !element) return 0;
  const scrollBoxRect = scrollBox.getBoundingClientRect();
  const scrollBoxTop = scrollBoxRect.top;
  const elementTop = element.getBoundingClientRect().top;
  return elementTop - scrollBoxTop;
}
export function getScrollOffsetBottom(scrollBox: HTMLDivElement | undefined | null, element: HTMLElement | undefined | null) {
  if (!scrollBox || !element) return 0;
  const scrollBoxRect = scrollBox.getBoundingClientRect();
  const scrollBoxTop = scrollBoxRect.top;
  const elementTop = element.getBoundingClientRect().bottom;
  return elementTop - scrollBoxTop;
}
type CssVar = string | number | null | undefined;
export type CssVars = Record<string, CssVar>;
export function useCssVars<E extends HTMLElement>(vars: CssVars) {
  const addPx = (value: CssVar) => (value?.constructor?.name === 'Number' ? `${value}px` : (value as string | null | undefined));
  const ref = useRef(null as E | null);
  return {
    __ref: ref,
    setRef: (node: E | null) => {
      ref.current = node;
      if (node) {
        Object.entries(vars).forEach(([k, v]) => {
          const key = `--${k}`;
          const valueWithUnit = addPx(v);
          if (valueWithUnit) node.style.setProperty(key, valueWithUnit);
          else node.style.removeProperty(key);
        });
      }
    },
  };
}
